import { useI18n } from '../../i18n';
// ============================================
// AtWorkPage - Danh sách cửa Nedap AtWork
// ============================================
// AtWorkPage - Danh sách cửa Nedap AtWork
// ============================================
//
// - Danh sách cửa: tên, site, store, trạng thái, nút "Mở cửa tạm thời".
// - Tổng quan biểu đồ lượt mở theo ngày (mục 3.4).
// - Bấm vào cửa để xem chi tiết (events / denied / audit).

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES, buildPath, PERMISSIONS } from '@conai/shared-constants';
import type { AtWorkDoor, AtWorkDailyStat } from '@conai/shared-types';
import { PageContainer, PageHeader } from '../../components/shared';
import { useHasPermission } from '../../stores/auth.store';
import { useAtWorkDoors, useAtWorkDailyStats, useProvideAccess } from '../../hooks';
import { ProvideAccessModal } from './components/ProvideAccessModal';
import { DailyAccessChart, type DailyAccessDatum } from './components/DailyAccessChart';
import { AtWorkHealthBadge } from './components/AtWorkHealthBadge';
import { RecentDeniedPanel } from './components/RecentDeniedPanel';
import { DoorCommandsPanel } from './components/DoorCommandsPanel';

function StatusBadge({ isActive }: { isActive: boolean }) {
  const { t } = useI18n();
  return isActive ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
      <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
      {t('active')}
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
      {t('suspended')}
    </span>
  );
}

/** Gộp các dòng (ngày × cửa) thành tổng theo ngày để vẽ tổng quan */
function aggregateByDay(stats: AtWorkDailyStat[]): DailyAccessDatum[] {
  const map = new Map<string, DailyAccessDatum>();
  for (const row of stats) {
    const cur = map.get(row.day) ?? { day: row.day, open_count: 0, denied_count: 0 };
    cur.open_count += row.open_count;
    cur.denied_count += row.denied_count;
    map.set(row.day, cur);
  }
  return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day));
}

export function AtWorkPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const canOpen = useHasPermission(PERMISSIONS.ATWORK_OPEN);

  const { data: doors, isLoading, error } = useAtWorkDoors();
  const { data: dailyStats } = useAtWorkDailyStats();
  const provideAccess = useProvideAccess();

  const [selectedDoor, setSelectedDoor] = useState<AtWorkDoor | null>(null);

  const chartData = useMemo(() => aggregateByDay(dailyStats ?? []), [dailyStats]);

  const handleConfirmOpen = (reason: string) => {
    if (!selectedDoor) return;
    provideAccess.mutate(
      {
        doorId: selectedDoor.atwork_door_id,
        data: { reason: reason || undefined, source: 'ADMIN_WEB' },
      },
      {
        onSuccess: () => setSelectedDoor(null),
      }
    );
  };

  const goToDetail = (door: AtWorkDoor) => {
    navigate(buildPath(ADMIN_ROUTES.ATWORK_DOOR_DETAIL, { doorId: door.atwork_door_id }));
  };

  return (
    <PageContainer>
      <PageHeader
        title={t('doorsAccessNedap')}
        subtitle={t('doorAccessControl')}
        actions={<AtWorkHealthBadge />}
      />

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('failedToLoadDoors')}</p>
        </div>
      )}

      {/* Tổng quan biểu đồ */}
      <div className="mt-6">
        <DailyAccessChart data={chartData} title={t('doorOpeningsOverTime')} />
      </div>

      {/* Danh sách cửa */}
      <div className="mt-6 overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-4">
          <h3 className="text-base font-bold text-slate-900">{t('doorList')}</h3>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-10">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
          </div>
        ) : !doors || doors.length === 0 ? (
          <div className="p-10 text-center text-slate-500">{t('noDoorsFound')}</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('doorName')}</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('site')}</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('accesscontrolStore')}</th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('status')}</th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">{t('action')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {doors.map((door) => (
                <tr
                  key={door.id}
                  className="cursor-pointer transition-colors hover:bg-slate-50"
                  onClick={() => goToDetail(door)}
                >
                  <td className="px-6 py-4">
                    <span className="font-medium text-slate-900">{door.name}</span>
                    <span className="block text-xs text-slate-400">{door.atwork_door_id}</span>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{door.site_name}</td>
                  <td className="px-6 py-4 text-slate-600">
                    {door.store ? (
                      <span className="text-slate-700">{t('mapped')}</span>
                    ) : (
                      <span className="text-slate-400">{t('notMapped')}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <StatusBadge isActive={door.is_active} />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDoor(door);
                      }}
                      disabled={!canOpen || !door.is_active}
                      title={!canOpen ? t('noPermissionToOpenDoor') : undefined}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {t('openTemporarily')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Events từ chối gần đây + Audit lệnh mở cửa remote toàn hệ thống */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <RecentDeniedPanel limit={50} />
        <DoorCommandsPanel limit={100} />
      </div>

      {/* Modal xác nhận */}
      {selectedDoor && (
        <ProvideAccessModal
          door={selectedDoor}
          isSubmitting={provideAccess.isPending}
          onConfirm={handleConfirmOpen}
          onClose={() => setSelectedDoor(null)}
        />
      )}
    </PageContainer>
  );
}
